import React from 'react'
import { shallow } from 'enzyme'
import { Tab } from '../Tab'
import humanizedLoanStates from '../helpers/humanizedLoanStates'
import loan from './testData/loan'

describe('<Tab />', () => {
  let wrapper, instance, props
  const currentYear = new Date().getFullYear()
  // Get current year plus the prior four years
  // This is outside of beforeEach because it has a significant impact on performance
  // Years should not be modified by the Tab component at this time,
  // if it does in the future, this may need to be refreshed for each test
  const years = [...Array(5).keys()].map(i => currentYear - i).reverse()
  beforeEach(() => {
    props = {
      loanId: loan.rid,
      businessId: loan.businesses[0].rid,
      requireAdditionalCashflowAnalysis: true,
      smartbizUser: true,
      years,
      data: {
        loan
      }
    }
    wrapper = shallow(<Tab {...props} />)
    instance = wrapper.instance()
  })

  describe('#hideCashflowAnalysis', ()=> {
    it('when hideCashflowAnalysis is false', () => {
      const { hideCashflowAnalysis } = wrapper.instance();

      expect(hideCashflowAnalysis).toBeFalsy()
    })

    it('when hideCashflowAnalysis is true', () => {
      props.requireAdditionalCashflowAnalysis = false
      props.smartbizUser = false
      wrapper.setProps({ ...props })

      const { hideCashflowAnalysis } = wrapper.instance()

      expect(hideCashflowAnalysis).toBeTruthy()
    })
  })

  describe('#getRedecisionText', () => {
    let oldState, newState, humanizedOldState, humanizedNewState
    describe('when the loan moves to pending decline', () => {
      beforeEach(() => {
        oldState = 'started'
        newState = 'pending_declined'
        humanizedOldState = humanizedLoanStates[oldState]
        humanizedNewState = humanizedLoanStates[newState]
      })
      it('should return a message that contains the humanized verison of pending_declined', () => {
        expect(instance.getRedecisionText(oldState, newState).includes(humanizedNewState)).toBeTruthy()
      })
    })
    describe('when the loan moves out of pending decline', () => {
      beforeEach(() => {
        oldState = 'pending_declined'
        newState = 'started'
        humanizedOldState = humanizedLoanStates[oldState]
        humanizedNewState = humanizedLoanStates[newState]
      })
      it('returns a message with humanized versions of pending_declined and the new state', () => {
        expect(instance.getRedecisionText(oldState, newState).includes(humanizedOldState)).toBeTruthy()
        expect(instance.getRedecisionText(oldState, newState).includes(humanizedNewState)).toBeTruthy()
      })
    })
    describe('when the loan doesn\'t change state', () => {
      beforeEach(() => {
        oldState = 'started'
        newState = 'started'
        humanizedOldState = humanizedLoanStates[oldState]
        humanizedNewState = humanizedLoanStates[newState]
      })
      it('returns a message with its current state', () => {
        expect(instance.getRedecisionText(oldState, newState).includes(humanizedOldState)).toBeTruthy()
        expect(instance.getRedecisionText(oldState, newState).includes(humanizedNewState)).toBeTruthy()
      })
    })
    describe('when the loan state changes', () => {
      beforeEach(() => {
        oldState = 'started'
        newState = 'prequalified'
        humanizedOldState = humanizedLoanStates[oldState]
        humanizedNewState = humanizedLoanStates[newState]
      })
      it('returns a message with a humanized version of the new loan state', () => {
        expect(instance.getRedecisionText(oldState, newState).includes(humanizedNewState)).toBeTruthy()
      })
    })
  })
  describe('#changePane', () => {
    it('should set the pane state property to the value of the provided argument', () => {
      const pane = 'global'
      instance.changePane(pane)
      expect(wrapper.state('pane')).toBe(pane)
    })
  })
  describe('#toggleTinkerTool', () => {
    describe('when isTinkerToolOpen is false', () => {
      beforeEach(() => {
        wrapper.setState({ isTinkerToolOpen: false })
      })
      it('should change isTinkerToolOpen to true', () => {
        instance.toggleTinkerTool()
        expect(wrapper.state('isTinkerToolOpen')).toBeTruthy()
      })
    })
    describe('when isTinkerToolOpen is true', () => {
      beforeEach(() => {
        wrapper.setState({ isTinkerToolOpen: true })
      })
      it('should change isTinkerToolOpen to false', () => {
        instance.toggleTinkerTool()
        expect(wrapper.state('isTinkerToolOpen')).toBeFalsy()
      })
    })
  })
  describe('#toggleUseOfProceeds', () => {
    describe('when isUseOfProceedsOpen is false', () => {
      beforeEach(() => {
        wrapper.setState({ isUseOfProceedsOpen: false })
      })
      it('should change isUseOfProceedsOpen to true', () => {
        instance.toggleUseOfProceeds()
        expect(wrapper.state('isUseOfProceedsOpen')).toBeTruthy()
      })
    })
    describe('when isUseOfProceedsOpen is true', () => {
      beforeEach(() => {
        wrapper.setState({ isUseOfProceedsOpen: true })
      })
      it('should change isUseOfProceedsOpen to false', () => {
        instance.toggleUseOfProceeds()
        expect(wrapper.state('isUseOfProceedsOpen')).toBeFalsy()
      })
    })
  })
  describe('#ajustmentsRef', () => {
    it('should set the instance\'s adjustments variable to the provided argument', () => {
      instance.adjustmentsRef(true)
      expect(instance.adjustments).toBeTruthy()
    })
  })
  describe('#resetDecisionModal', () => {
    describe('when isRedecisioning and isDoneDecisioning are true and redecisionResultText contains some text', () => {
      beforeEach(() => {
        wrapper.setState({
          isRedecisioning: true,
          isDoneDecisioning: true,
          redecisionResultTest: 'test'
        })
      })
      it('should change isRedecisioning and isDoneDecisioning to false and redecisionResultsText to an empty string', () => {
        instance.resetDecisionModal()
        expect(wrapper.state('isRedecisioning')).toBeFalsy()
        expect(wrapper.state('isDoneDecisioning')).toBeFalsy()
        expect(wrapper.state('redecisionResultText')).toBe('')
      })
    })
  })
  describe('#renderRedecisionModal', () => {
    describe('when isRedecisioning is true', () => {
      beforeEach(() => wrapper.setState({ isRedecisioning: true }))
      it('should return the redecision modal', () => {
        expect(instance.renderRedecisionModal()).toBeTruthy()
      })
    })
    describe('when isRedecisioning is false', () => {
      beforeEach(() => wrapper.setState({ isRedecisioning: false }))
      it('should return undefined', () => {
        expect(instance.renderRedecisionModal()).toBeUndefined()
      })
    })
    describe('when isDoneDecisioning is true', () => {
      beforeEach(() => wrapper.setState({ isDoneDecisioning: true }))
      it('should return the redecison modal', () => {
        expect(instance.renderRedecisionModal()).toBeTruthy()
      })
    })
    describe('when isDoneDecisioning is false', () => {
      beforeEach(() => wrapper.setState({ isDoneDecisioning: false }))
      it('should return undefined', () => {
        expect(instance.renderRedecisionModal()).toBeUndefined()
      })
    })
  })
  describe('#renderStaticTables', () => {
    describe('when the selected pane is not `global`', () => {
      beforeEach(() => wrapper.setState({ pane: 0 }))
      it('should return <StaticTables />', () => {
        expect(instance.renderStaticTables()).toBeTruthy()
      })
    })
    describe('when the selected pane is `global`', () => {
      it('should return undefined', () => {
        expect(instance.renderStaticTables()).toBeTruthy()
      })
    })
  })
})